import { parseEnvironment, isProd, isEU, isUS, type ParsedEnvironment } from '@goldcast/utils';

import { getEnv } from '@/utils/env';

let _parsedEnv: ParsedEnvironment | null = null;
export function getAppEnvironment(): ParsedEnvironment {
  if (!_parsedEnv) {
    _parsedEnv = parseEnvironment(getEnv('VUE_APP_CVENT_ENV') || 'local');
  }
  return _parsedEnv;
}

export function isObjEmpty(obj): boolean {
  if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
    throw new Error('Parameter must be an object');
  }
  return Object.entries(obj).length === 0;
}

export function isValidJson(text: any) {
  try {
    JSON.parse(text);
    return true;
  } catch (error) {
    return false;
  }
}

export function copyToClipboard(textToCopy) {
  return new Promise((resolve, reject) => {
    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        resolve('Copied');
      })
      .catch(err => {
        reject(err);
      });
  });
}

export const imageSrcPath = (str: string): string => `${getEnv('VUE_APP_ASSETS_CDN')}/${str}`;

export function isEURegion(): boolean {
  return isPr53Env();
}

export function isAppEnvironmentProduction(): boolean {
  return isProd(getAppEnvironment());
}

export function isUSRegion(): boolean {
  return isUS(getAppEnvironment());
}

export function isPr53Env(): boolean {
  return getEnv('VUE_APP_ENVIRONMENT') === 'pr53' && getEnv('VUE_APP_ENV_REGION') === 'eu';
}

export const EU_VIDEO_LIBRARY_CUTOFF = new Date('2026-06-20');

export function shouldUseVideoLibraryForEU(eventStartTime?: string): boolean {
  return isPr53Env() && !!eventStartTime && new Date(eventStartTime) >= EU_VIDEO_LIBRARY_CUTOFF;
}

/**
 * Returns true when the event should use the Video Library pipeline.
 * Old PR53 events (before EU_VIDEO_LIBRARY_CUTOFF) are excluded — the VL
 * pipeline never ran for them so HLS/edit features would be invalid.
 */
export function isVLReadyEvent(eventStartTime?: string): boolean {
  return !isPr53Env() || shouldUseVideoLibraryForEU(eventStartTime);
}

export function snakeCaseToCapitalized(text) {
  return text
    .split('_')
    .map(word => {
      if (!word) {
        return word;
      }
      return word[0].toUpperCase() + word.slice(1);
    })
    .join(' ');
}

export const isObject = (value: any): boolean => {
  return typeof value === 'object' && !Array.isArray(value) && value !== null;
};

export function downloadFileByMimeType(content: string, fileName: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
